import React, { useEffect, useRef } from 'react';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import i18n from 'i18n-js';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import PropTypes from 'prop-types';
import { BaseModal } from 'components/UI';
import { makeStyles } from '@material-ui/core/styles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import * as Yup from 'yup';
import { withFormik } from 'formik';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import VersionFileInput from './VersionFileInput';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import { KeyboardDatePicker } from '@material-ui/pickers';
import clsx from 'clsx';
import 'react-image-lightbox/style.css';
import useDeepCompareEffect from 'use-deep-compare-effect';

const useStyles = makeStyles((theme) => ({
  input: {
    height: 70,
    margin: 0,
  },
  select: {
    minWidth: '100%',
  },
  filesLabel: {
    color: '#4D4F5C',
    marginBottom: 15,
  },
  label: {
    color: '#2C2C2E',
  },
  leftInputcontainer: {
    [theme.breakpoints.up('sm')]: {
      paddingRight: theme.spacing(2),
    },
  },
  rightInputContainer: {
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(2),
    },
  },
}));

const VersionSchema = Yup.object().shape({
  version: Yup.string().required(),
  status: Yup.string(),
  expiration_time: Yup.date().nullable(),
  description: Yup.string(),
  default: Yup.boolean(),
  file: Yup.mixed(),
});

const VersionModal = ({
  open,
  handleClose,
  touched,
  errors,
  values,
  handleChange,
  handleBlur,
  submitForm,
  setFieldValue,
  mode,
  resetForm,
  asyncErrors = {},
  setErrors,
  setFieldTouched,
}) => {
  const defaultRef = useRef(null);
  const classes = useStyles();
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  useEffect(() => {
    if (values && defaultRef.current === null) {
      defaultRef.current = values.default;
      setFieldValue('allowSetStatus', values.default);
    }
    // eslint-disable-next-line
  }, []);

  useDeepCompareEffect(() => {
    if (asyncErrors) {
      setErrors(asyncErrors);
      Object.keys(asyncErrors).map((field) => setFieldTouched(field, true, false));
    }
  }, [asyncErrors, setErrors, setFieldTouched]);

  return (
    <BaseModal
      size="md"
      open={open}
      hasFooter={false}
      enableCloseButton={false}
      aria-labelledby="modal-title"
      title={
        mode === 'create'
          ? i18n.t('documents.create_new_version')
          : i18n.t('documents.edit_version')
      }
    >
      <form autoComplete="off">
        <Grid container direction="column" alignItems="flex-start">
          <Grid container spacing={1}>
            <Grid container item xs={12}>
              <Grid item xs={12}>
                <TextField
                  className={classes.input}
                  required
                  fullWidth
                  name="version"
                  error={Boolean(touched.version && errors.version)}
                  label={i18n.t('documents.version_name')}
                  helperText={
                    Boolean(touched.version && errors.version) ? errors.version : undefined
                  }
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.version || ''}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid container item xs={12}>
              {(mode === 'create' || values.allowSetStatus) && (
                <Grid item xs={12} sm={6} className={classes.leftInputcontainer}>
                  <FormControl className={clsx(classes.input, classes.select)}>
                    <InputLabel id="status-select-label">{i18n.t('common.status')}</InputLabel>
                    <Select
                      labelId="status-select-label"
                      value={values.status || ''}
                      name="status"
                      onChange={handleChange}
                    >
                      <MenuItem value={'APPROVED'}>{i18n.t('documents.approved')}</MenuItem>
                      <MenuItem value={'DECLINED'}>{i18n.t('documents.declined')}</MenuItem>
                      <MenuItem value={'PENDING'}>{i18n.t('documents.pending')}</MenuItem>
                      <MenuItem value={'UPLOADED'}>{i18n.t('documents.uploaded')}</MenuItem>
                      <MenuItem value={'NONE'}></MenuItem>
                    </Select>
                    {errors.status && touched.status ? (
                      <FormHelperText error>{errors.status}</FormHelperText>
                    ) : null}
                  </FormControl>
                </Grid>
              )}
              <Grid
                item
                xs={12}
                sm={6}
                className={
                  mode === 'create' || values?.allowSetStatus
                    ? classes.rightInputContainer
                    : classes.leftInputcontainer
                }
              >
                <KeyboardDatePicker
                  fullWidth
                  className={classes.input}
                  margin="normal"
                  id="date-picker-dialog"
                  label={i18n.t('documents.expiration_date')}
                  format="MM/DD/yyyy"
                  value={values.expiration_time}
                  onChange={(value) => setFieldValue('expiration_time', value)}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                />
                {errors.expiration_time && touched.expiration_time ? (
                  <FormHelperText error>{errors.expiration_time}</FormHelperText>
                ) : null}
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid container item xs={12}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rowsMax={4}
                  inputProps={{ maxLength: 512 }}
                  name="description"
                  error={Boolean(touched.description && errors.description)}
                  label={i18n.t('common.description')}
                  helperText={
                    Boolean(touched.description && errors.description)
                      ? errors.description
                      : undefined
                  }
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.description || ''}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid container item xs={12}>
              <Grid item xs={12}>
                <Box mt={3}>
                  <FormControlLabel
                    name="default"
                    control={
                      <Checkbox
                        color="primary"
                        checked={values.default || false}
                        onChange={handleChange}
                      />
                    }
                    label={
                      <Typography className={classes.label} variant="body2">
                        {i18n.t('documents.set_as_default_version')}
                      </Typography>
                    }
                    labelPlacement="end"
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
          {mode === 'create' ? (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Box mt={2}>
                  <Typography className={classes.filesLabel} variant="subtitle2" gutterBottom>
                    {values.file
                      ? i18n.t('documents.version_attachment')
                      : `${i18n.t('documents.upload_file')} (${i18n.t('common.optional')})`}
                  </Typography>
                </Box>
                <VersionFileInput file={values.file} onChange={setFieldValue} name="file" />
              </Grid>
            </Grid>
          ) : null}
          <Box mt={2.5} width="100%" role="group" display="flex" justifyContent="flex-end">
            <Box display="inline" ml={1}>
              <Button
                color="primary"
                onClick={handleClose}
                style={{ letterSpacing: 0.7, fontWeight: 500 }}
              >
                {i18n.t('documents.cancel')}
              </Button>
            </Box>
            <Box display="inline" ml={1}>
              <Button
                color="primary"
                onClick={submitForm}
                style={{ letterSpacing: 0.7, fontWeight: 500 }}
              >
                {mode === 'create' ? i18n.t('documents.create') : i18n.t('documents.update')}
              </Button>
            </Box>
          </Box>
        </Grid>
      </form>
    </BaseModal>
  );
};

VersionModal.propTypes = {
  /**
   * Current state modal state
   */
  open: PropTypes.bool,
  /**
   * Define the action to by applied
   */
  mode: PropTypes.oneOf(['create', 'update']),
  /**
   * Close click handler
   */
  handleClose: PropTypes.func,
  /**
   * Create click handler
   */
  handleCreate: PropTypes.func,
  /**
   * Version's name
   */
  version: PropTypes.string,
  /**
   * Version's status
   */
  status: PropTypes.string,
  /**
   * Version's expiration date
   */
  expiration_time: PropTypes.string,
  /**
   * Versions' description
   */
  description: PropTypes.string,
  /**
   * Version's file
   */
  file: PropTypes.any,
  /**
   * Version should be marked as default
   */
  default: PropTypes.any,
};

VersionModal.defaultProps = {
  open: false,
  handleClose: undefined,
  handleCreate: undefined,
  version: undefined,
  status: '',
  expiration_time: null,
  description: undefined,
  file: undefined,
  default: false,
};

export default withFormik({
  mapPropsToValues: (props) => ({
    version: props.version,
    status: props.status || '',
    expiration_time: props.expiration_time || null,
    description: props.description,
    file: props.file,
    default: props.default,
    mode: props.mode || 'create',
    allowSetStatus: props.allowSetStatus,
  }),
  displayName: 'VersionModalForm',
  validationSchema: VersionSchema,
  handleSubmit: (
    {
      file,
      mode,
      status,
      expiration_time,
      default: isDefault,
      description,
      allowSetStatus,
      ...values
    },
    { props: { handleCreate } }
  ) =>
    handleCreate({
      ...values,
      ...(typeof isDefault === 'boolean' && { default: isDefault }),
      ...(expiration_time && {
        expiration_time,
      }),
      ...(status &&
        (mode === 'create' || allowSetStatus) && {
          status,
        }),
      ...(file && {
        data: file,
      }),
      ...(description && {
        description,
      }),
    }),
})(VersionModal);
